/** Feature-intro overlay. Surfaces ONE pending FeatureIntro at a
 *  time as a dismissible card in the bottom-right of the workspace.
 *  Distinct visual + structural treatment from the first-run
 *  onboarding wizard at /onboarding so a returning user can never
 *  confuse "here is a new feature" with "set up your account from
 *  scratch."
 *
 *  Mount once at the workspace shell level (App.tsx). The component
 *  fetches dismissed keys from /api/feature-intros/seen, crosses
 *  them against FEATURE_INTROS in the registry, and shows whatever
 *  is left. Dismissing posts to /api/feature-intros/dismiss. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X as XIcon, Sparkles, ArrowRight } from "lucide-react";
import { useAuth, getCsrfToken } from "../lib/auth";
import { pendingFeatureIntros, type FeatureIntro } from "../lib/featureIntros";

export function FeatureIntroOverlay() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState<FeatureIntro[] | null>(null);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (auth.loading || !auth.authenticated) return;
    // First-run users get the wizard at /onboarding. Skip the
    // overlay until they finish, otherwise both surfaces compete.
    if (!auth.user?.onboardedAt) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/feature-intros/seen", { credentials: "same-origin" });
        if (!res.ok) return;
        const body = (await res.json()) as { seen?: { feature_key: string }[] };
        const dismissed = new Set((body.seen || []).map((r) => r.feature_key));
        const next = pendingFeatureIntros({
          dismissedKeys: dismissed,
          onboardedAt: auth.user?.onboardedAt ?? null,
        });
        if (cancelled) return;
        setPending(next);
        // Record the first impression for the head of the queue so
        // shown_at is captured even if the user closes the tab
        // before dismissing.
        if (next.length > 0) {
          const csrf = getCsrfToken();
          void fetch("/api/feature-intros/mark-shown", {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
              ...(csrf ? { "X-CSRF-Token": csrf } : {}),
            },
            body: JSON.stringify({ feature_key: next[0].key }),
          });
        }
      } catch {
        /* network blip — overlay just stays hidden, no error UI */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.loading, auth.authenticated, auth.user?.onboardedAt]);

  if (!pending || pending.length === 0) return null;
  const intro = pending[0];

  const dismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      const csrf = getCsrfToken();
      await fetch("/api/feature-intros/dismiss", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify({ feature_key: intro.key }),
      });
      setPending((p) => (p || []).slice(1));
    } catch {
      /* best-effort: drop the head locally so the user isn't stuck
       * on a server hiccup; next mount will re-fetch dismissed and
       * surface again if the dismiss never landed */
      setPending((p) => (p || []).slice(1));
    } finally {
      setDismissing(false);
    }
  };

  const acceptCta = async () => {
    if (!intro.ctaRoute) return;
    await dismiss();
    navigate(intro.ctaRoute);
  };

  return (
    <div className="fixed bottom-4 right-4 z-40 max-w-sm">
      <div className="rounded-2xl border border-white/15 bg-[#0c2540]/95 backdrop-blur-xl shadow-2xl p-4 space-y-3">
        <div className="flex items-start gap-3">
          <span className="w-8 h-8 rounded-lg bg-sky-400/15 border border-sky-400/30 flex items-center justify-center text-sky-200 flex-shrink-0">
            <Sparkles className="w-4 h-4" />
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-white">{intro.title}</h3>
            <p className="text-xs text-slate-300/80 mt-1">{intro.summary}</p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            disabled={dismissing}
            aria-label="Dismiss"
            className="text-slate-400 hover:text-white flex-shrink-0 disabled:opacity-30"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          {intro.cta && intro.ctaRoute ? (
            <button
              type="button"
              onClick={acceptCta}
              disabled={dismissing}
              className="flex-1 rounded-lg bg-[#f97316] hover:bg-[#fb923c] text-white text-xs font-semibold px-3 py-2 inline-flex items-center justify-center gap-1 disabled:opacity-50"
            >
              {intro.cta} <ArrowRight className="w-3 h-3" />
            </button>
          ) : null}
          <button
            type="button"
            onClick={dismiss}
            disabled={dismissing}
            className="flex-1 rounded-lg bg-white/10 border border-white/15 text-white text-xs font-semibold px-3 py-2 hover:bg-white/20 disabled:opacity-50"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
